import { Heading } from '@efdigital/ll-addon-ui';
import { EmptyState } from '@efdigital/ll-xceed-v2/components/application/empty-state';
import { Spinner } from '@efdigital/ll-xceed-v2/components/application/spinner.gts';
import CoacheeCard from '@efdigital/ll-xceed-v2/components/coaching/coach/coachee-card.gts';
import DashboardSessionRow from '@efdigital/ll-xceed-v2/components/coaching/coach/dashboard-session-row.gts';
import { OnboardingChecklist } from '@efdigital/ll-xceed-v2/components/coaching/coach/onboarding-checklist';
import { DashboardStat } from '@efdigital/ll-xceed-v2/components/coaching/dashboard-stat.gts';
import { queryCoachingPartnershipsByCoach } from '@efdigital/ll-xceed-v2/data/builders/coaching/coaching-partnerships';
import { queryCoachingSessionsByCoach } from '@efdigital/ll-xceed-v2/data/builders/coaching/coaching-sessions';
import type { CoachingCoachModel } from '@efdigital/ll-xceed-v2/routes/coaching/coach';
import type CoachService from '@efdigital/ll-xceed-v2/services/coach';
import type SessionService from '@efdigital/ll-xceed-v2/services/session';
import {
  buildCoacheeList,
  COACH_COACHEE_PARTNERSHIP_KEYS,
} from '@efdigital/ll-xceed-v2/utils/coaching/coach-coachees';
import { toCoachSessionCardItems } from '@efdigital/ll-xceed-v2/utils/coaching/coach-session-card-items';
import {
  COACH_ACTION_REQUIRED_QUERY_KEYS,
  COACH_PENDING_CONFIRMATION_KEYS,
  COACH_UPCOMING_KEYS,
  filterCoachActionRequired,
} from '@efdigital/ll-xceed-v2/utils/coaching/session-current-categories';
import { PAGE_WRAPPER } from '@efdigital/ll-xceed-v2/utils/layout';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';
import { Await } from '@warp-drive/ember';

/** Dashboard cards never show more than this many rows at once. */
const DASHBOARD_PREVIEW_LIMIT = 3;

/** Extra flagged sessions to fetch before client-side filtering. */
const DASHBOARD_FLAGS_QUERY_HEADROOM = 10;

function takeDashboardPreview<T>(items: readonly T[]): T[] {
  return items.slice(0, DASHBOARD_PREVIEW_LIMIT);
}

interface IndexSignature {
  Args: {
    model: CoachingCoachModel;
    controller: unknown;
  };
}

const MAX = 20;

export default class CoachingCoachIndexComponent extends Component<IndexSignature> {
  @service declare coach: CoachService;
  @service declare store: Store;
  @service declare session: SessionService;

  async getCoachees() {
    const engagements = await this.store.request(
      queryCoachingPartnershipsByCoach(
        this.session.cached.userId!,
        ['chemistry', 'chosen'],
        { offset: 0, limit: MAX + 1 }
      )
    );

    return {
      count: Math.min(engagements.content.data.length, MAX),
      more: engagements.content.data.length > MAX,
    };
  }

  async getPendingConfirmation() {
    const sessions = await this.store.request(
      queryCoachingSessionsByCoach(
        this.session.cached.userId!,
        COACH_PENDING_CONFIRMATION_KEYS,
        { offset: 0, limit: MAX + 1 }
      )
    );
    return {
      count: Math.min(sessions.content.data.length, MAX),
      more: sessions.content.data.length > MAX,
    };
  }

  async getUpcomingSessions() {
    const sessions = await this.store.request(
      queryCoachingSessionsByCoach(
        this.session.cached.userId!,
        COACH_UPCOMING_KEYS,
        { offset: 0, limit: MAX + 1 }
      )
    );
    return {
      count: Math.min(sessions.content.data.length, MAX),
      more: sessions.content.data.length > MAX,
      next: sessions.content.data[0],
    };
  }

  async getActionRequired() {
    const sessions = await this.store.request(
      queryCoachingSessionsByCoach(
        this.session.cached.userId!,
        COACH_ACTION_REQUIRED_QUERY_KEYS,
        { offset: 0, limit: MAX + 1 }
      )
    );
    const actionRequired = filterCoachActionRequired(sessions.content.data);
    return {
      count: Math.min(actionRequired.length, MAX),
      more: actionRequired.length > MAX,
    };
  }

  async getSessionsDelivered() {
    const sessions = await this.store.request(
      queryCoachingSessionsByCoach(
        this.session.cached.userId!,
        ['attended', 'no_show_coachee'],
        { offset: 0, limit: MAX + 1 }
      )
    );
    return {
      count: Math.min(sessions.content.data.length, MAX),
      more: sessions.content.data.length > MAX,
    };
  }

  getUpcomingSessionsPreview = async () => {
    const sessions = await this.store.request(
      queryCoachingSessionsByCoach(
        this.session.cached.userId!,
        COACH_UPCOMING_KEYS,
        { offset: 0, limit: DASHBOARD_PREVIEW_LIMIT }
      )
    );
    const items = await toCoachSessionCardItems(
      takeDashboardPreview(sessions.content.data),
      this.coach
    );

    return takeDashboardPreview(items);
  };

  getPendingFlagsPreview = async () => {
    const coachUserId = this.session.cached.userId!;
    const [pendingConfirmation, actionRequiredQuery] = await Promise.all([
      this.store.request(
        queryCoachingSessionsByCoach(
          coachUserId,
          COACH_PENDING_CONFIRMATION_KEYS,
          { offset: 0, limit: DASHBOARD_PREVIEW_LIMIT }
        )
      ),
      this.store.request(
        queryCoachingSessionsByCoach(
          coachUserId,
          COACH_ACTION_REQUIRED_QUERY_KEYS,
          { offset: 0, limit: DASHBOARD_FLAGS_QUERY_HEADROOM }
        )
      ),
    ]);

    const actionRequired = filterCoachActionRequired(
      actionRequiredQuery.content.data
    );
    const items = await toCoachSessionCardItems(
      // Action-required sessions are more urgent than pending-confirmation ones,
      // so they go first -- otherwise a full page of pending-confirmation sessions
      // could push every action-required session out of the sliced preview.
      takeDashboardPreview([
        ...actionRequired,
        ...pendingConfirmation.content.data,
      ]),
      this.coach
    );

    return items;
  };

  getCoacheesPreview = async () => {
    const coachUserId = this.session.cached.userId!;
    const [partnerships, sessions, coacheeNames] = await Promise.all([
      this.store.request(
        queryCoachingPartnershipsByCoach(
          coachUserId,
          COACH_COACHEE_PARTNERSHIP_KEYS
        )
      ),
      this.store.request(queryCoachingSessionsByCoach(coachUserId)),
      this.coach.getCoachees(),
    ]);

    const coachees = buildCoacheeList(
      partnerships.content.data,
      sessions.content.data,
      coacheeNames
    );

    return {
      items: takeDashboardPreview(coachees),
      count: coachees.length,
    };
  };

  getStats = async () => {
    const [
      coachees,
      pendingConfirmation,
      upcomingSessions,
      actionRequired,
      sessionsDelivered,
    ] = await Promise.all([
      this.getCoachees(),
      this.getPendingConfirmation(),
      this.getUpcomingSessions(),
      this.getActionRequired(),
      this.getSessionsDelivered(),
    ]);

    return {
      coachees,
      pendingConfirmation,
      upcomingSessions,
      actionRequired,
      sessionsDelivered,
    };
  };

  <template>
    <div class={{PAGE_WRAPPER}}>
      <Heading
        @size="lg"
        @text="Welcome, {{@model.coach.firstName}}"
        @secondary="Here's your coaching practice at a glance."
        @class="mb-4 ll-page-heading"
      />

      <OnboardingChecklist @coach={{@model.coach}} class="mb-4">
        <Await @promise={{(this.getStats)}}>
          <:success as |stats|>

            <div class="row mb-4 g-3">
              <div class="col-6 col-lg-4 col-xl">
                <DashboardStat
                  @title="Coachees"
                  @value={{stats.coachees}}
                  @icon="ph:user"
                  class="h-100"
                />
              </div>
              <div class="col-6 col-lg-4 col-xl">
                <DashboardStat
                  @title="Pending confirmation"
                  @value={{stats.pendingConfirmation}}
                  @icon="ph:clock"
                  class="h-100"
                />
              </div>
              <div class="col-6 col-lg-4 col-xl">
                <DashboardStat
                  @title="Upcoming sessions"
                  @value={{stats.upcomingSessions}}
                  @icon="ph:calendar"
                  class="h-100"
                />
              </div>
              <div class="col-6 col-lg-4 col-xl">
                <DashboardStat
                  @title="Action required"
                  @value={{stats.actionRequired}}
                  @icon="ph:warning"
                  @variant="attention"
                  class="h-100"
                />
              </div>
              <div class="col-6 col-lg-4 col-xl">
                <DashboardStat
                  @title="Credits delivered"
                  @value={{stats.sessionsDelivered}}
                  @icon="ph:credit-card"
                  class="h-100"
                />
              </div>
            </div>
          </:success>
          <:pending>
            <Spinner />
          </:pending>
        </Await>
      </OnboardingChecklist>

      <div class="row g-3 g-lg-4 mb-4">
        <div class="col-12 col-xl-6">
          <section aria-labelledby="upcoming-sessions-heading" class="h-100">
            <Await @promise={{(this.getUpcomingSessionsPreview)}}>
              <:success as |sessions|>
                <div
                  class="card border shadow-sm rounded-4 h-100 overflow-hidden"
                >
                  <div class="card-body p-3 p-md-4">
                    <div
                      class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3"
                    >
                      <h2
                        id="upcoming-sessions-heading"
                        class="h5 fw-bold mb-0 min-w-0 text-break text-body-emphasis"
                      >
                        Upcoming sessions
                      </h2>
                      <LinkTo
                        @route="coaching.coach.sessions"
                        class="btn btn-link link-primary text-decoration-none p-0 fw-semibold flex-shrink-0"
                      >View all<span class="visually-hidden">
                          upcoming sessions</span></LinkTo>
                    </div>

                    {{#if sessions.length}}
                      <div class="d-flex flex-column gap-2">
                        {{#each sessions as |item|}}
                          <DashboardSessionRow
                            @session={{item.session}}
                            @coachee={{item.coachee}}
                          />
                        {{/each}}
                      </div>
                    {{else}}
                      <EmptyState @message="You have no upcoming sessions." />
                    {{/if}}
                  </div>
                </div>
              </:success>
              <:pending>
                <Spinner />
              </:pending>
            </Await>
          </section>
        </div>

        <div class="col-12 col-xl-6">
          <section aria-labelledby="pending-flags-heading" class="h-100">
            <Await @promise={{(this.getPendingFlagsPreview)}}>
              <:success as |flags|>
                <div
                  class="card border shadow-sm rounded-4 h-100 overflow-hidden"
                >
                  <div class="card-body p-3 p-md-4">
                    <div
                      class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3"
                    >
                      <h2
                        id="pending-flags-heading"
                        class="h5 fw-bold mb-0 min-w-0 text-break text-body-emphasis"
                      >
                        Need your attention
                      </h2>
                      <LinkTo
                        @route="coaching.coach.sessions.action-required"
                        class="btn btn-link link-primary text-decoration-none p-0 fw-semibold flex-shrink-0"
                      >View all<span class="visually-hidden">
                          sessions needing attention</span></LinkTo>
                    </div>

                    {{#if flags.length}}
                      <div class="d-flex flex-column gap-2">
                        {{#each flags as |item|}}
                          <DashboardSessionRow
                            @session={{item.session}}
                            @coachee={{item.coachee}}
                            @alert={{true}}
                          />
                        {{/each}}
                      </div>
                    {{else}}
                      <EmptyState @message="Nothing needs your attention." />
                    {{/if}}
                  </div>
                </div>
              </:success>
              <:pending>
                <Spinner />
              </:pending>
            </Await>
          </section>
        </div>
      </div>

      <section aria-labelledby="dashboard-coachees-heading">
        <Await @promise={{(this.getCoacheesPreview)}}>
          <:success as |preview|>
            <div
              class="card border shadow-sm rounded-4 overflow-hidden"
              data-test-coach-dashboard-coachees
            >
              <div class="card-body p-3 p-md-4">
                <div
                  class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3"
                >
                  <h2
                    id="dashboard-coachees-heading"
                    class="h5 fw-bold mb-0 min-w-0 text-break text-body-emphasis"
                  >
                    Coachees
                    {{#if preview.count}}
                      <span
                        class="text-body-secondary fw-normal"
                      >{{preview.count}}</span>
                    {{/if}}
                  </h2>
                  <LinkTo
                    @route="coaching.coach.coachees"
                    class="btn btn-link link-primary text-decoration-none p-0 fw-semibold flex-shrink-0"
                  >View all<span class="visually-hidden">
                      coachees</span></LinkTo>
                </div>

                {{#if preview.items.length}}
                  <div class="row g-3 align-items-stretch">
                    {{#each preview.items as |coachee|}}
                      <div class="col-12 col-sm-6 col-xl-4">
                        <CoacheeCard @coachee={{coachee}} />
                      </div>
                    {{/each}}
                  </div>
                {{else}}
                  <EmptyState
                    @message="You have no coachees yet. They appear here once a learner shortlists you for a chemistry call."
                    @icon="ph:users"
                  />
                {{/if}}
              </div>
            </div>
          </:success>
          <:pending>
            <Spinner />
          </:pending>
        </Await>
      </section>
    </div>
  </template>
}
